import React, { useState } from 'react';
import { Plus, Trash2, Image as ImageIcon, Palette, Grid, List, Star, Type as TypeIcon, ChevronUp, ChevronDown, X, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAlert } from '../../../../context/AlertContext';

const MenuEditor = ({ menu, onChange, onOpenMediaModal }) => {
    const { t } = useTranslation();
    const { showConfirm } = useAlert();
    const [collapsedCats, setCollapsedCats] = useState({});

    const updateField = (field, value) => onChange({ ...menu, [field]: value });

    const updateDesign = (field, value) => {
        onChange({ ...menu, designRecommendation: { ...(menu.designRecommendation || {}), [field]: value } });
    };

    const updateCategory = (cIdx, field, value) => {
        const cats = [...(menu.categories || [])];
        cats[cIdx] = { ...cats[cIdx], [field]: value };
        onChange({ ...menu, categories: cats });
    };

    const deleteCategory = async (cIdx) => {
        if (await showConfirm('Bu kategoriyi silmek istediğinize emin misiniz? (İçindeki ürünler de bu formdan kaybolur)', { title: 'Kategoriyi Sil', confirmText: 'Sil', type: 'error' })) {
            const cats = menu.categories.filter((_, i) => i !== cIdx);
            onChange({ ...menu, categories: cats });
        }
    };

    const toggleCat = (cIdx) => setCollapsedCats(prev => ({...prev, [cIdx]: !prev[cIdx]}));

    const moveCategory = (cIdx, dir) => {
        if (cIdx + dir < 0 || cIdx + dir >= menu.categories.length) return;
        const cats = [...menu.categories];
        const temp = cats[cIdx];
        cats[cIdx] = cats[cIdx + dir];
        cats[cIdx + dir] = temp;
        onChange({ ...menu, categories: cats });
    };

    const addCategory = () => {
        const newCats = [...(menu.categories || []), { categoryName: 'Yeni Kategori', items: [] }];
        onChange({ ...menu, categories: newCats });
        setCollapsedCats(prev => ({...prev, [newCats.length - 1]: false}));
    };

    const updateItem = (cIdx, iIdx, field, value) => {
        const cats = [...menu.categories];
        const items = [...cats[cIdx].items];
        items[iIdx] = { ...items[iIdx], [field]: value };
        cats[cIdx] = { ...cats[cIdx], items };
        onChange({ ...menu, categories: cats });
    };

    const deleteItem = async (cIdx, iIdx) => {
        if (await showConfirm('Bu ürünü menü yapısından çıkarmak istediğinize emin misiniz?', { title: 'Ürünü Çıkar', confirmText: 'Çıkar', type: 'warning' })) {
            const cats = [...menu.categories];
            cats[cIdx].items = cats[cIdx].items.filter((_, i) => i !== iIdx);
            onChange({ ...menu, categories: cats });
        }
    };

    const moveItem = (cIdx, iIdx, dir) => {
        if (iIdx + dir < 0 || iIdx + dir >= menu.categories[cIdx].items.length) return;
        const cats = [...menu.categories];
        const items = [...cats[cIdx].items];
        const temp = items[iIdx];
        items[iIdx] = items[iIdx + dir];
        items[iIdx + dir] = temp;
        cats[cIdx].items = items;
        onChange({ ...menu, categories: cats });
    };
    
    const removeCategoryImage = (e, cIdx) => {
        e.stopPropagation();
        updateCategory(cIdx, 'image', '');
    };

    const iconBtnStyle = { background: 'var(--bg-card)', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: '8px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s', padding: 0 };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                <div>
                    <label style={{ display: 'block', marginBottom: '0.875rem', fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-muted)' }}>{t('menu.menuName')}</label>
                    <input value={menu.menuName || ''} onChange={e => updateField('menuName', e.target.value)} placeholder={t('menu.menuName')} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-main)' }} />
                </div>
                <div>
                    <label style={{ display: 'block', marginBottom: '0.875rem', fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-muted)' }}>{t('menu.description')}</label>
                    <input value={menu.description || ''} onChange={e => updateField('description', e.target.value)} placeholder="Menü hakkında kısa bir açıklama..." style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-main)' }} />
                </div>
            </div>

            {/* Design Config */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '2.5rem', marginTop: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
                        <Palette size={20} color="var(--primary)" /> {t('menu.design', 'Tasarım ve Görünüm')}
                    </h3>
                    <button
                        onClick={async () => {
                            const confirmed = await showConfirm(
                                'Renk ve font ayarları fabrika değerlerine döndürülecek. Devam etmek istiyor musunuz?',
                                { title: 'Varsayılana Dön', confirmText: 'Sıfırla', type: 'warning' }
                            );
                            if (confirmed) {
                                onChange({
                                    ...menu,
                                    designRecommendation: {
                                        ...(menu.designRecommendation || {}),
                                        primaryColor: '#6f4e37',
                                        bgColor: '#f5f5f7',
                                        cardBgColor: '#ffffff',
                                        textColor: '#2c1a12',
                                        fontStyle: 'sans-serif',
                                    }
                                });
                            }
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: '12px', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer' }}
                    >
                        <RotateCcw size={14} /> Varsayılan
                    </button>
                </div>

                {/* Template Selector */}
                <div style={{ marginBottom: '2.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '1rem', fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-muted)' }}>Menü Şablonu</label>
                    <div className="template-selector-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                        {[
                            { id: 'classic', label: 'Klasik', icon: Grid, desc: 'Kategori grid + büyük kartlar' },
                            { id: 'elegant', label: 'Elegant', icon: Star, desc: 'Öne çıkan ürünler, tek sütun' },
                            { id: 'compact', label: 'Kompakt', icon: List, desc: 'Liste görünümü, yatay satırlar' },
                            { id: 'minimal', label: 'Minimal', icon: TypeIcon, desc: 'Sade tipografi, görselsiz' }
                        ].map(tmpl => {
                            const isActive = (menu.designRecommendation?.template || 'classic') === tmpl.id;
                            return (
                                <div
                                    key={tmpl.id}
                                    onClick={() => updateDesign('template', tmpl.id)}
                                    style={{
                                        padding: '1.25rem 1rem',
                                        borderRadius: '16px',
                                        border: isActive ? '2px solid var(--primary)' : '2px solid var(--border)',
                                        background: isActive ? 'rgba(var(--primary-rgb), 0.05)' : 'var(--bg-card)',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        textAlign: 'center',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}
                                >
                                    <div style={{
                                        width: '44px', height: '44px', borderRadius: '12px',
                                        background: isActive ? 'var(--primary)' : 'var(--bg-main)',
                                        color: isActive ? 'white' : 'var(--text-muted)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        <tmpl.icon size={20} />
                                    </div>
                                    <div style={{ fontWeight: 800, fontSize: '0.875rem', color: isActive ? 'var(--primary)' : 'var(--text-main)' }}>{tmpl.label}</div>
                                    <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', lineHeight: 1.3 }}>{tmpl.desc}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '2rem' }}>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.875rem', fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-muted)' }}>İkon ve Fiyat Rengi</label>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <input type="color" value={(menu.designRecommendation?.primaryColor || '#6f4e37').trim()} onChange={e => updateDesign('primaryColor', e.target.value)} style={{ width: '40px', height: '40px', padding: '4px', borderRadius: '10px', border: '1px solid var(--border)', cursor: 'pointer', background: 'transparent' }} />
                            <input type="text" value={(menu.designRecommendation?.primaryColor || '#6f4e37').trim()} onChange={e => updateDesign('primaryColor', e.target.value)} style={{ border: 'none', background: 'transparent', fontWeight: 700, fontFamily: 'monospace', width: '70px', padding: 0, outline: 'none', color: 'var(--text-main)' }} />
                        </div>
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.875rem', fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-muted)' }}>{t('menu.bgColor', 'Arkaplan Rengi')}</label>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <input type="color" value={(menu.designRecommendation?.bgColor || '#f5f5f7').trim()} onChange={e => updateDesign('bgColor', e.target.value)} style={{ width: '40px', height: '40px', padding: '4px', borderRadius: '10px', border: '1px solid var(--border)', cursor: 'pointer', background: 'transparent' }} />
                            <input type="text" value={(menu.designRecommendation?.bgColor || '#f5f5f7').trim()} onChange={e => updateDesign('bgColor', e.target.value)} style={{ border: 'none', background: 'transparent', fontWeight: 700, fontFamily: 'monospace', width: '70px', padding: 0, outline: 'none', color: 'var(--text-main)' }} />
                        </div>
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.875rem', fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-muted)' }}>Kart & Yüzey Rengi</label>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <input type="color" value={(menu.designRecommendation?.cardBgColor || '#ffffff').trim()} onChange={e => updateDesign('cardBgColor', e.target.value)} style={{ width: '40px', height: '40px', padding: '4px', borderRadius: '10px', border: '1px solid var(--border)', cursor: 'pointer', background: 'transparent' }} />
                            <input type="text" value={(menu.designRecommendation?.cardBgColor || '#ffffff').trim()} onChange={e => updateDesign('cardBgColor', e.target.value)} style={{ border: 'none', background: 'transparent', fontWeight: 700, fontFamily: 'monospace', width: '70px', padding: 0, outline: 'none', color: 'var(--text-main)' }} />
                        </div>
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.875rem', fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-muted)' }}>Font Rengi</label>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <input type="color" value={(menu.designRecommendation?.textColor || '#2c1a12').trim()} onChange={e => updateDesign('textColor', e.target.value)} style={{ width: '40px', height: '40px', padding: '4px', borderRadius: '10px', border: '1px solid var(--border)', cursor: 'pointer', background: 'transparent' }} />
                            <input type="text" value={(menu.designRecommendation?.textColor || '#2c1a12').trim()} onChange={e => updateDesign('textColor', e.target.value)} style={{ border: 'none', background: 'transparent', fontWeight: 700, fontFamily: 'monospace', width: '70px', padding: 0, outline: 'none', color: 'var(--text-main)' }} />
                        </div>
                    </div>
                    <div style={{ gridColumn: '1 / -1', marginTop: '1rem', borderTop: '1px solid var(--border)', paddingTop: '1.5rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.875rem', fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-muted)' }}>{t('menu.fontStyle', 'Font Tipi')}</label>
                        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px' }}>
                            {[
                                { id: 'sans-serif', label: 'Modern', sub: 'Inter' },
                                { id: 'serif', label: 'Classic', sub: 'Georgia' },
                                { id: 'playfair', label: 'Premium', sub: 'Playfair Display' },
                                { id: 'outfit', label: 'Soft', sub: 'Outfit' },
                                { id: 'montserrat', label: 'Minimal', sub: 'Montserrat' }
                            ].map(font => (
                                <button 
                                    key={font.id} 
                                    onClick={() => updateDesign('fontStyle', font.id)} 
                                    style={{ 
                                        flex: '1 0 auto',
                                        minWidth: '110px',
                                        padding: '10px', 
                                        borderRadius: '12px', 
                                        background: (menu.designRecommendation?.fontStyle || 'sans-serif') === font.id ? 'var(--primary)' : 'var(--bg-main)', 
                                        color: (menu.designRecommendation?.fontStyle || 'sans-serif') === font.id ? 'white' : 'var(--text-main)', 
                                        border: '1px solid var(--border)', 
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '2px'
                                    }}
                                >
                                    <span style={{ fontWeight: 800, fontSize: '0.85rem' }}>{font.label}</span>
                                    <span style={{ fontSize: '0.65rem', opacity: 0.7 }}>{font.sub}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

                {/* Featured Section Editor */}
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '2.5rem', marginTop: '2.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.5rem' }}>
                        <Star size={20} color="#f59e0b" fill="#f59e0b" />
                        <h3 style={{ fontSize: '1.25rem', fontWeight: 800 }}>Öne Çıkanlar Ayarları</h3>
                    </div>

                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.875rem', fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-muted)' }}>Bölüm Başlığı</label>
                        <input 
                            value={menu.designRecommendation?.featuredTitle || 'Öne Çıkanlar'} 
                            onChange={e => updateDesign('featuredTitle', e.target.value)} 
                            placeholder="Örn: En Popülerler, Şefin Seçimi..." 
                            style={{ width: '100%', maxWidth: '400px', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-main)' }} 
                        />
                    </div>

                    {/* Featured Item List (Order only, toggle is in Products tab) */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '0.875rem', fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-muted)' }}>Sıralamayı Düzenle</label>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>Öne çıkarılan ürünlerin sırasını aşağı-yukarı oklarla belirleyebilirsiniz. (Ürün eklemek için yan taraftaki <b>Ürünler</b> sekmesindeki yıldız butonunu kullanın)</p>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {(() => {
                                const featuredSet = [];
                                (menu.categories || []).forEach((cat, cIdx) => {
                                    (Array.isArray(cat.items) ? cat.items : []).forEach((item, iIdx) => {
                                        if (item.featured) featuredSet.push({ ...item, cIdx, iIdx });
                                    });
                                });

                                // Check if we have an explicit featuredOrder array, if so, sort by it
                                const featuredOrder = menu.designRecommendation?.featuredOrder || [];
                                featuredSet.sort((a, b) => {
                                    const aIdx = featuredOrder.indexOf(`${a.cIdx}-${a.iIdx}`);
                                    const bIdx = featuredOrder.indexOf(`${b.cIdx}-${b.iIdx}`);
                                    if (aIdx === -1 && bIdx === -1) return 0;
                                    if (aIdx === -1) return 1;
                                    if (bIdx === -1) return -1;
                                    return aIdx - bIdx;
                                });

                                const moveFeatured = (idx, step) => {
                                    const keys = featuredSet.map(f => `${f.cIdx}-${f.iIdx}`);
                                    if (idx + step < 0 || idx + step >= keys.length) return;
                                    const newOrder = [...keys];
                                    const temp = newOrder[idx];
                                    newOrder[idx] = newOrder[idx + step];
                                    newOrder[idx + step] = temp;
                                    updateDesign('featuredOrder', newOrder);
                                };

                                if (featuredSet.length === 0) {
                                    return <div style={{ padding: '1.5rem', border: '1px dashed var(--border)', borderRadius: '12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>Hiç öne çıkarılan ürün yok.</div>;
                                }

                                return featuredSet.map((f, fIdx) => (
                                    <div key={`${f.cIdx}-${f.iIdx}`} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px' }}>
                                        <div style={{ width: '40px', height: '40px', borderRadius: '8px', overflow: 'hidden', background: 'var(--bg-main)' }}>
                                            <img src={f.image || 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=100&h=100&fit=crop'} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</div>
                                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{menu.categories[f.cIdx].categoryName}</div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '4px' }}>
                                            <button onClick={() => moveFeatured(fIdx, -1)} disabled={fIdx === 0} style={{ ...iconBtnStyle, width: '28px', height: '28px', opacity: fIdx === 0 ? 0.3 : 1 }}>
                                                <ArrowUp size={14} />
                                            </button>
                                            <button onClick={() => moveFeatured(fIdx, 1)} disabled={fIdx === featuredSet.length - 1} style={{ ...iconBtnStyle, width: '28px', height: '28px', opacity: fIdx === featuredSet.length - 1 ? 0.3 : 1 }}>
                                                <ArrowDown size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ));
                            })()}
                        </div>
                    </div>
                </div>

            {/* Structure Config */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '2.5rem', marginTop: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <List size={20} color="var(--primary)" /> Menü Düzeni ve Başlıkları
                        </h3>
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '4px' }}>Yeni kategoriler oluşturun, başlıkları isimlendirin ve sıralamayı ayarlayın.</p>
                    </div>
                    <button onClick={addCategory} className="btn-primary" style={{ padding: '8px 16px', borderRadius: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', border: 'none' }}>
                        <Plus size={16} /> Kategori Ekle
                    </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                    {!(menu.categories?.length > 0) && (
                        <div className="card glass" style={{ padding: '3rem 1.5rem', textAlign: 'center', opacity: 0.6 }}>
                            <p>Henüz kategori eklemediniz.</p>
                        </div>
                    )}
                    
                    {Array.isArray(menu.categories) && menu.categories.map((cat, cIdx) => (
                        <div key={cIdx} className="category-container" style={{ background: 'var(--bg-main)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
                            {/* Category Header */}
                            <div style={{ padding: '0.75rem 1.25rem', borderBottom: collapsedCats[cIdx] ? 'none' : '1px solid var(--border)', background: 'var(--bg-card)', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <button disabled={cIdx === 0} onClick={() => moveCategory(cIdx, -1)} className="hover-text-primary" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-muted)' }}><ChevronUp size={16}/></button>
                                    <button disabled={cIdx === menu.categories.length - 1} onClick={() => moveCategory(cIdx, 1)} className="hover-text-primary" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-muted)' }}><ChevronDown size={16}/></button>
                                </div>
                                
                                <div style={{ flex: 1, minWidth: '150px' }}>
                                    <input value={cat.categoryName} onChange={e => updateCategory(cIdx, 'categoryName', e.target.value)} placeholder="Kategori Adı" style={{ width: '100%', fontSize: '1rem', fontWeight: 800, background: 'transparent', border: 'none', color: 'var(--text-main)', outline: 'none' }} />
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{cat.items?.length || 0} Ürün (Düzenlemek için tıklayın)</div>
                                </div>
                                
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <button onClick={() => onOpenMediaModal('category', cIdx)} style={{...iconBtnStyle, color: cat.image ? 'var(--primary)' : 'var(--text-muted)', borderColor: cat.image ? 'var(--primary)' : 'var(--border)'}} title="Kategori Görseli (Opsiyonel)">
                                        <ImageIcon size={16} />
                                    </button>
                                    {cat.image && (
                                        <button onClick={(e) => removeCategoryImage(e, cIdx)} style={{...iconBtnStyle, color: '#ef4444'}} title="Görseli Kaldır">
                                            <X size={16} />
                                        </button>
                                    )}
                                    <div style={{ width: '1px', height: '20px', background: 'var(--border)', margin: '0 4px' }} />
                                    <button onClick={() => deleteCategory(cIdx)} className="hover-bg-danger" style={{...iconBtnStyle, color: '#ef4444'}} title="Kategoriyi Sil"><Trash2 size={16} /></button>
                                    <button onClick={() => toggleCat(cIdx)} style={iconBtnStyle} title={collapsedCats[cIdx] ? 'Ürünleri Gizle' : 'Ürünleri Göster'}>
                                        {collapsedCats[cIdx] ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                                    </button>
                                </div>
                            </div>

                            {/* Compact Items List for Sorting */}
                            {!collapsedCats[cIdx] && (
                                <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', background: 'var(--bg-main)' }}>
                                    {Array.isArray(cat.items) && cat.items.map((item, iIdx) => (
                                        <div key={iIdx} style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: 'var(--bg-card)', padding: '0.75rem 1rem', borderRadius: '8px', border: '1px solid var(--border)' }}>
                                            {/* Item Sorting Arrows */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <button disabled={iIdx === 0} onClick={() => moveItem(cIdx, iIdx, -1)} className="hover-text-primary" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0px', color: 'var(--text-muted)' }}><ChevronUp size={14}/></button>
                                                <button disabled={iIdx === cat.items.length - 1} onClick={() => moveItem(cIdx, iIdx, 1)} className="hover-text-primary" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0px', color: 'var(--text-muted)' }}><ChevronDown size={14}/></button>
                                            </div>
                                            
                                            <div style={{ flex: 1, fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {item.name || 'İsimsiz Ürün'}
                                            </div>
                                            
                                            <button 
                                                onClick={() => updateItem(cIdx, iIdx, 'featured', !item.featured)} 
                                                className="hover-text-primary" 
                                                style={{ 
                                                    background: 'transparent', 
                                                    border: 'none', 
                                                    cursor: 'pointer', 
                                                    padding: '4px', 
                                                    color: item.featured ? '#f59e0b' : 'var(--text-muted)',
                                                    transition: 'all 0.2s'
                                                }} 
                                                title={item.featured ? "Öne Çıkarılanlardan Kaldır" : "Öne Çıkar"}
                                             >
                                                 <Star size={16} fill={item.featured ? '#f59e0b' : 'transparent'} />
                                             </button>
                                             
                                             <button onClick={() => deleteItem(cIdx, iIdx)} className="hover-text-danger" style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', cursor: 'pointer', padding: '4px' }} title="Ürünü Sil">
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ))}
                                    {(!cat.items || cat.items.length === 0) && (
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: '0.5rem', textAlign: 'center' }}>
                                            Bu kategoride hiç ürün yok. Detaylar ve ürün eklemek için 'Ürünler' sekmesine geçin.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <style>{`
                .hover-text-primary:hover { color: var(--primary) !important; }
                .hover-bg-danger:hover { background: rgba(239, 68, 68, 0.1) !important; color: #ef4444 !important; border-color: transparent !important; }
                .hover-text-danger:hover { color: #ef4444 !important; }
                
                button:disabled { opacity: 0.3 !important; cursor: not-allowed !important; }
                
                @media (max-width: 768px) {
                    .template-selector-grid { grid-template-columns: repeat(2, 1fr) !important; }
                }
                @media (max-width: 600px) {
                    .responsive-grid { grid-template-columns: 1fr !important; }
                }
            `}</style>
        </div>
    );
};

export default MenuEditor;